<script setup lang="ts">
/***
 * Imports
 */
import { watch } from 'vue'
import { useEditor, EditorContent, VueNodeViewRenderer } from '@tiptap/vue-3'
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import { lowlight } from 'lowlight/lib/core'

import TipTapCodeBlock from './TipTapCodeBlock.vue'

/***
 * Types
 */

/***
 * Props
 */
const props = defineProps({
  editable: {
    type: Boolean,
    default: true
  },
  modelValue: {
    type: String,
    default: ''
  },
  plain: {
    type: Boolean,
    default: false
  },
  bold: {
    type: Boolean,
    default: true
  },
  italic: {
    type: Boolean,
    default: true
  },
  underline: {
    type: Boolean,
    default: true
  },
  paragraph: {
    type: Boolean,
    default: true
  },
  h1: {
    type: Boolean,
    default: false
  },
  h2: {
    type: Boolean,
    default: false
  },
  h3: {
    type: Boolean,
    default: false
  },
  h4: {
    type: Boolean,
    default: true
  },
  h5: {
    type: Boolean,
    default: false
  },
  h6: {
    type: Boolean,
    default: false
  },
  bulletlist: {
    type: Boolean,
    default: true
  },
  orderedlist: {
    type: Boolean,
    default: false
  },
  codeblock: {
    type: Boolean,
    default: true
  },
  undo: {
    type: Boolean,
    default: false
  },
  redo: {
    type: Boolean,
    default: false
  }
})

/***
 * Data / Refs
 */
const emit = defineEmits(['update:modelValue'])

const editor = useEditor({
  content: props.modelValue,
  extensions: [
    TiptapStarterKit.configure({
      codeBlock: false // override the default code block
    }),
    CodeBlockLowlight.extend({
      addNodeView() {
        // @ts-ignore
        return VueNodeViewRenderer(TipTapCodeBlock)
      }
    }).configure({ lowlight })
  ],
  autofocus: false,
  injectCSS: false,
  editable: props.editable,
  editorProps: {
    attributes: {
      class: 'editor-content'
    }
  },

  onUpdate({ editor }) {
    // The content has changed.
    if (props.plain) {
      emit('update:modelValue', editor.getText())
      console.log(editor.getText())
    } else {
      emit('update:modelValue', editor.getHTML())
    }
  }
})

watch(
  () => props.modelValue,
  () => {
    if (!props.editable && editor.value) editor.value.commands.setContent(props.modelValue)
  }
)

const css = (await import('highlight.js/lib/languages/css')).default
const js = (await import('highlight.js/lib/languages/javascript')).default
const ts = (await import('highlight.js/lib/languages/typescript')).default
const html = (await import('highlight.js/lib/languages/xml')).default
const python = (await import('highlight.js/lib/languages/python')).default

lowlight.registerLanguage('html', html)
lowlight.registerLanguage('css', css)
lowlight.registerLanguage('javascript', js)
lowlight.registerLanguage('typescript', ts)
lowlight.registerLanguage('python', python)
</script>

<template>
  <div id="tiptap-editor" class="tiptap-editor">
    <div v-if="props.editable && editor && !props.plain" class="editor-toolbar">
      <button
        v-if="h4"
        tabindex="-1"
        type="button"
        aria-label="H1"
        class="toolbar-button"
        :class="{ active: editor.isActive('heading', { level: 4 }) }"
        @click="editor.chain().focus().toggleHeading({ level: 4 }).run()"
      >
        <CommonIconTextEditorHeading />
        <div class="tool-tip anim-fade-down">Heading</div>
      </button>
      <button
        v-if="h5"
        tabindex="-1"
        type="button"
        aria-label="H2"
        class="toolbar-button"
        :class="{ active: editor.isActive('heading', { level: 5 }) }"
        @click="editor.chain().focus().toggleHeading({ level: 5 }).run()"
      >
        H2
      </button>
      <button
        v-if="h6"
        tabindex="-1"
        type="button"
        aria-label="H3"
        class="toolbar-button"
        :class="{ active: editor.isActive('heading', { level: 6 }) }"
        @click="editor.chain().focus().toggleHeading({ level: 6 }).run()"
      >
        H3
      </button>
      <button
        v-if="paragraph"
        tabindex="-1"
        type="button"
        aria-label="P"
        class="toolbar-button"
        :class="{ active: editor.isActive('paragraph') }"
        @click="editor.chain().focus().setParagraph().run()"
      >
        <CommonIconTextEditorP />
        <div class="tool-tip anim-fade-down">Text</div>
      </button>
      <button
        v-if="bold"
        type="button"
        tabindex="-1"
        aria-label="Bold"
        :disabled="!editor.can().chain().focus().toggleBold().run()"
        class="toolbar-button"
        :class="{ active: editor.isActive('bold') }"
        @click="editor.chain().focus().toggleBold().run()"
      >
        <CommonIconTextEditorBold />
        <div class="tool-tip anim-fade-down">Bold</div>
      </button>
      <button
        v-if="italic"
        tabindex="-1"
        type="button"
        aria-label="Italic"
        :disabled="!editor.can().chain().focus().toggleItalic().run()"
        class="toolbar-button"
        :class="{ active: editor.isActive('italic') }"
        @click="editor.chain().focus().toggleItalic().run()"
      >
        <CommonIconTextEditorItalic />
        <div class="tool-tip anim-fade-down">Italic</div>
      </button>
      <!-- <button
        v-if="underline"
        tabindex="-1"
        type="button"
        aria-label="Underline"
        :disabled="!editor.can().chain().focus().toggleUnderline().run()"
        class="toolbar-button"
        :class="{ active: editor.isActive('underline') }"
        @click="editor.chain().focus().toggleUnderline().run()"
      >
        <CommonIconTextEditorUnderline />
        <div class="tool-tip anim-fade-down">Underline</div>
      </button> -->
      <button
        v-if="bulletlist"
        tabindex="-1"
        type="button"
        aria-label="BulletList"
        class="toolbar-button"
        :class="{ active: editor.isActive('bulletList') }"
        @click="editor.chain().focus().toggleBulletList().run()"
      >
        <CommonIconTextEditorUl class="size-l" />
        <div class="tool-tip anim-fade-down">Unordered list</div>
      </button>
      <button
        v-if="orderedlist"
        tabindex="-1"
        type="button"
        aria-label="OrderedList"
        class="toolbar-button"
        :class="{ active: editor.isActive('orderedList') }"
        @click="editor.chain().focus().toggleOrderedList().run()"
      >
        <CommonIconTextEditorOl class="size-l" />
        <div class="tool-tip anim-fade-down">Ordered list</div>
      </button>
      <button
        v-if="codeblock"
        tabindex="-1"
        type="button"
        aria-label="CodeBlock"
        class="toolbar-button"
        :class="{ active: editor.isActive('codeBlock') }"
        @click="editor.commands.toggleCodeBlock()"
      >
        <CommonIconTextEditorCode class="size-l" />
        <div class="tool-tip anim-fade-down">Code block</div>
      </button>
    </div>
    <EditorContent :editor="editor" :value="props.modelValue" :model-value="props.modelValue" />
  </div>
</template>

<style lang="scss" scoped>
#tiptap-editor {
  text-align: left;

  .editor-toolbar {
    display: flex;
    flex-wrap: wrap;
    border-bottom: 1px solid var(--borderM2);
    padding: 7px 10px;
    gap: 3px;
    background: var(--bgLight);
    border-top-left-radius: 7px;
    border-top-right-radius: 7px;

    .toolbar-button {
      min-width: 0;
      width: 40px;
      height: 35px;
      border-radius: 5px;
      background-color: transparent;
      border-color: transparent;
      color: var(--textM2);
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;

      svg {
        $svgSize: 14px;
        height: $svgSize;
        width: $svgSize;
        fill: var(--textM2);
      }

      .size-l {
        $svgSize: 20px;
        height: $svgSize;
        width: $svgSize;
      }

      .tool-tip {
        position: absolute;
        background: white;
        padding: 10px 12px;
        border-radius: 7px;
        font-size: 0.85rem;
        color: white;
        background: rgba(0, 0, 0, 0.8);
        box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
        top: calc(100% + 5px);
        display: none;
        font-weight: 400;
        isolation: isolate;
        z-index: 10;
      }

      $purple-900: #4c1d95;

      @media (hover: hover) {
        &:hover {
          border-color: var(--borderM2);
        }
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      &.active {
        background-color: rgb(208, 219, 229);
        color: var(--black);

        svg {
          fill: var(--black);
        }

        .tool-tip {
          color: white;
        }
      }
    }

    @media (hover: hover) {
      .toolbar-button:hover {
        .tool-tip {
          display: block;
        }
      }
    }
  }

  :deep(.editor-content) {
    min-height: 200px;
    max-height: 50dvh;
    overflow-y: scroll;
    padding: 15px;
    background: white;
    border-bottom-left-radius: 7px;
    border-bottom-right-radius: 7px;
  }
}

:deep(.ProseMirror) {
  &:focus-visible {
    outline: none;
  }

  > * + * {
    margin-top: 0.75em;
  }

  p {
    font-size: var(--text-t5);
    font-weight: 300;
  }

  ul {
    list-style: disc;
  }

  ol {
    list-style: decimal;
  }

  ul,
  ol {
    padding-left: 1rem;

    li p {
      padding-left: 1rem;
    }
  }

  h4 {
    /* font-size: var(--text-h4); */
    font-weight: 500;
    line-height: 1.1;
    font-size: 1.3rem;
    line-height: 1.8rem;
  }

  pre {
    background: #141928;
    color: #fff;
    font-family: 'JetBrainsMono', monospace;
    padding: 0.75rem 1rem;
    border-radius: 0.5rem;

    code {
      color: inherit;
      padding: 0;
      background: none;
      font-size: 0.8rem;
    }
  }

  img {
    max-width: 100%;
    height: auto;
  }

  blockquote {
    padding-left: 1rem;
    border-left: 2px solid rgba(#0d0d0d, 0.1);
  }

  hr {
    border: none;
    border-top: 2px solid rgba(#0d0d0d, 0.1);
    margin: 2rem 0;
  }

  .code-block {
    select {
      background: rgba(255, 255, 255, 0.8);
      background: transparent;
      border-color: transparent;
      color: white;
      height: 25px;
      padding: 0 10px;
      top: 8px;
      right: 15px;
      border-radius: 0;
      font-size: 0.9rem;
    }

    pre {
      background: #141928;
      color: #fff;
      font-family: monospace;
      padding: 40px 0 0;
      border-radius: 7px;
      min-height: 150px;

      code {
        color: inherit;
        padding: 0;
        background: none;
        border-top: 1px solid rgb(89, 106, 124);
        padding: 20px;
      }

      code > * {
        font-size: 0.9rem;
        line-height: 1.3rem;
        font-family: monospace;

        span {
          font-size: 0.9rem;
          line-height: 1.3rem;
          font-family: monospace;
        }
      }

      .hljs-comment,
      .hljs-quote {
        color: #616161;
      }

      .hljs-variable,
      .hljs-template-variable,
      .hljs-attribute,
      .hljs-tag,
      .hljs-name,
      .hljs-regexp,
      .hljs-link,
      .hljs-name,
      .hljs-selector-id,
      .hljs-selector-class {
        color: #f98181;
      }

      .hljs-number,
      .hljs-meta,
      .hljs-built_in,
      .hljs-builtin-name,
      .hljs-literal,
      .hljs-type,
      .hljs-params {
        color: #fbbc88;
      }

      .hljs-string,
      .hljs-symbol,
      .hljs-bullet {
        color: #b9f18d;
      }

      .hljs-title,
      .hljs-section {
        color: #faf594;
      }

      .hljs-keyword,
      .hljs-selector-tag {
        color: #70cff8;
      }

      .hljs-emphasis {
        font-style: italic;
      }

      .hljs-strong {
        font-weight: 700;
      }
    }
  }
}

@media (max-width: 500px) {
  #tiptap-editor {
    .editor-toolbar {
      padding: 5px;

      .toolbar-button {
        width: 35px;
      }
    }
  }
}

@media (max-width: 400px) {
  #tiptap-editor {
    .editor-toolbar {
      justify-content: space-between;

      .toolbar-button {
        height: 32px;
        flex: 1;
      }
    }
  }
}
</style>
